/**
 * CommandDispatcherRef
 * Late-bound handle to the SDK's CommandDispatcher (FragmentCommandManager).
 *
 * State is constructed before the fragment-module exists, so state sub-modules
 * hold this reference and it is bound once the dispatcher has been created.
 */

import type { CommandMessageType, CommandPayloadByType } from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';
import type { CommandDispatcher, CommandTarget } from '@anddone/ajs-util';

const logger = createLogger('SDK', 'CommandDispatcherRef');

export class CommandDispatcherRef implements CommandDispatcher {
  #dispatcher: CommandDispatcher | null = null;

  /**
   * Bind the dispatcher. May only be called once so the command path cannot
   * be swapped out after initialization.
   *
   * @param dispatcher - The dispatcher to forward to
   * @returns void
   */
  public bind(dispatcher: CommandDispatcher): void {
    if (this.#dispatcher !== null) {
      throw new Error('CommandDispatcher has already been bound.');
    }

    this.#dispatcher = dispatcher;
  }

  public dispatch<Type extends CommandMessageType>(
    target: CommandTarget,
    type: Type,
    data: CommandPayloadByType[Type],
  ): boolean {
    if (!this.#dispatcher) {
      logger.warn('dispatch', `Skipped "${type}" — no command dispatcher bound yet.`, { target });
      return false;
    }

    return this.#dispatcher.dispatch(target, type, data);
  }

  public logToOrchestrator(message: string): void {
    this.#dispatcher?.logToOrchestrator(message);
  }

  public refreshOrchestratorStatus(): void {
    this.#dispatcher?.refreshOrchestratorStatus();
  }
}

Object.freeze(CommandDispatcherRef.prototype);
